import { useEffect, useRef } from 'react';

import { useConsumer } from '../lib/channels/hooks/use-consumer';

/**
 * Event types received from BizyChannel on the backend.
 */
export interface BizyTokenEvent {
	event: 'token';
	data: { content: string };
}

export interface BizyCompleteEvent {
	event: 'complete';
	data: {
		content: string;
		message_id: string | null;
		conversation_id: string;
		emotion: string;
		tools_used: string[];
		response_time_ms: number;
		first_token_ms: number | null;
	};
}

export interface BizyActionEvent {
	event: 'page_action';
	data: {
		action: string;
		report_type: string;
		page_path: string;
		params: Record<string, unknown>;
		report_view: string;
	};
}

export interface BizyErrorEvent {
	event: 'error';
	data: { message: string };
}

export interface BizyToolCallEvent {
	event: 'tool_call';
	data: { tool_name: string; arguments: Record<string, unknown> };
}

export interface BizyPreliminaryTokenEvent {
	event: 'preliminary_token';
	data: { content: string };
}

export type BizyChannelEvent = BizyTokenEvent | BizyCompleteEvent | BizyErrorEvent | BizyToolCallEvent | BizyPreliminaryTokenEvent | BizyActionEvent;

export interface UseBizyChannelOptions {
	onToken?: (content: string) => void;
	onComplete?: (data: BizyCompleteEvent['data']) => void;
	onError?: (message: string) => void;
	onToolCall?: (data: BizyToolCallEvent['data']) => void;
	onPreliminaryToken?: (content: string) => void;
	onPageAction?: (data: BizyActionEvent['data']) => void;
}

/**
 * Hook for subscribing to BizyChannel via ActionCable.
 *
 * Uses deferred subscription creation (setTimeout 0) to prevent React
 * StrictMode's double-mount from creating duplicate subscriptions.
 * In StrictMode: mount1 schedules → cleanup1 cancels → mount2 schedules →
 * mount2's timer fires → ONE subscription created.
 *
 * @param userId - The current user's ID (determines whether to subscribe)
 * @param options - Callbacks for token, complete, and error events
 */
export function useBizyChannel(userId: string | undefined, options: UseBizyChannelOptions) {
	const consumer = useConsumer();
	const subscriptionRef = useRef<any>(null);

	// Use refs for callbacks to avoid re-subscribing when callbacks change
	const optionsRef = useRef(options);
	optionsRef.current = options;

	useEffect(() => {
		if (!consumer || !userId) return;

		// Defer subscription creation so StrictMode's cleanup cancels the
		// first mount's timer before the subscription is ever created.
		// Only the second (real) mount's timer fires, creating exactly one
		// subscription.
		const timer = setTimeout(() => {
			const subscription = consumer.subscriptions.create(
				{ channel: 'BizyChannel' },
				{
					received: (message: BizyChannelEvent) => {
						if (!message) return;

						switch (message.event) {
							case 'token':
								optionsRef.current.onToken?.(message.data.content);
								break;
							case 'complete':
								optionsRef.current.onComplete?.(message.data);
								break;
							case 'error':
								optionsRef.current.onError?.(message.data.message);
								break;
							case 'tool_call':
								optionsRef.current.onToolCall?.(message.data);
								break;
							case 'preliminary_token':
								optionsRef.current.onPreliminaryToken?.(message.data.content);
								break;
							case 'page_action':
								optionsRef.current.onPageAction?.(message.data);
								break;
						}
					},
				}
			);

			subscriptionRef.current = subscription;
		}, 0);

		return () => {
			clearTimeout(timer);
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
		};
	}, [consumer, userId]);
}
